import usePlaygroundMode from '@/util/hooks/queries/usePlaygroundMode';

/**
 * Persistent, app-wide indicator that QA Playground Mode is on for this session
 * (Feature #8075, PBI #8080, AC3). Renders nothing when the session has it off, when the
 * status has never been confirmed (`isActive` defaults to false in both), or when the env
 * gate is confirmed off (`isUnavailable`, a 404).
 *
 * That last check matters on its own: React Query keeps the last successful `data` through
 * ANY later error, a 404 included, so `isActive` alone would keep reporting a stale "on" if
 * `QaPlaygroundEnabled` is turned off mid-session (config change + restart, or a redeploy)
 * — the toggle would correctly disappear while the banner kept claiming ON against a server
 * that had stopped suppressing (PR #2577 review, fp:banner-ignores-unavailable-404). A 404
 * is a definite answer, unlike a transient failure, which is deliberately NOT gated on here:
 * the server holds Playground Mode state independent of the browser, so a transient fetch
 * error must not make this banner disappear while the session may still be suppressing
 * (same PR, Koketso Ramokhubathi). Shows on/off state only — no other Playground Mode
 * detail belongs here (review note, Custom.OtherNotes #8080).
 */
const PlaygroundModeBanner = () => {
  const { isUnavailable, isActive } = usePlaygroundMode();

  if (isUnavailable || !isActive) {
    return null;
  }

  return (
    // <output> has an implicit "status" ARIA role (a polite live region), so this is
    // preferred over a <div role="status"> for cross-device accessibility (SonarQube
    // typescript:S6819). No sticky/z-index of its own: this is mounted as the first
    // child of each app shell wrapper in root.tsx, which is what stays visible and
    // positions it — a sibling with its own `sticky top-0` raced NavigationHeader's for
    // the same spot and, in the two fixed-height shells, added height on top of an
    // already-100dvh container (review, PR #2577, fp:banner-outside-app-shell).
    // #9141: exact styling from the design prototype ("real component, exact copy").
    <output className="block w-full bg-[#F59E0B] px-4 py-1.5 text-center text-[13px] font-bold text-[#5C3A00]">
      Playground Mode: ON
    </output>
  );
};

export default PlaygroundModeBanner;
